import type { OverlayRef } from '@angular/cdk/overlay';
import { type Signal, signal, type TemplateRef } from '@angular/core';
import { EMPTY, filter, merge, type Observable, ReplaySubject, takeUntil } from 'rxjs';

import { ESCAPE_KEY } from '../constants/escape-key.constant';
import type { ClarityFlyInEscape } from '../models/clarity-fly-in-escape.model';
import type { ClarityFlyInFocus } from '../models/clarity-fly-in-focus.model';
import { activeHtmlElement } from '../utils/active-html-element.util';
import { focusFirstFocusable } from '../utils/focus-first-focusable.util';
import { listenForDocumentEscape } from './listen-for-document-escape.util';

/** Dismissal is on unless the caller says otherwise, matching the dialog. */
const DEFAULT_DISMISSIBLE = true;

/** How long brain's `SidePanel` waits before it moves focus into a panel it opened (`SidePanel.tsx:7`). */
const FOCUS_DELAY_MS = 50;

/**
 * How a ported System Domain panel departs from the defaults, resolved by `ClarityFlyIn` from the
 * open config. Absent, the ref keeps its original contract: the CDK dispatcher's Escape and no focus
 * handling of its own (the container's trap owns focus).
 */
export type ClarityFlyInRefBehaviour = {
	readonly escape: ClarityFlyInEscape;
	readonly focus: ClarityFlyInFocus;
	readonly document: Document;
};

/** Brain's side panel stops every key but Escape at itself (`SidePanel.tsx:297-302`). */
function stopKeyUnlessEscape(event: KeyboardEvent): void {
	if (event.key !== ESCAPE_KEY) {
		event.stopPropagation();
	}
}

/** Ported from Clarity `apps/web/src/shared/ui/SidePanel.tsx` (commit `f9217bb5a`). */
/**
 * The caller's handle on an open Clarity fly-in, and the owner of the overlay's lifetime.
 *
 * A CDK overlay hands out a pane, a backdrop and a scroll strategy and nothing else, so unlike the
 * dialog this ref has to spell the dismissal contract itself. By default the stream is the house
 * shape — `merge(backdropClick(), keydownEvents() filtered to Escape)` — gated by a **mutable** flag
 * rather than by the value the config carried at open, which is what makes {@link setDismissible} a
 * live lock instead of an open-time option. `overlayRef.keydownEvents()` is routed by the CDK's
 * keyboard dispatcher to the top-most overlay, so Escape works wherever focus currently sits.
 *
 * A panel opened with a `document` Escape policy never subscribes to that dispatcher: its Escape is a
 * `document` listener as in brain (`ClarityFlyInEscape`), so the dispatcher keeps handing the same
 * keystroke to the exclusive panel beneath and both close together. The same flag gates it.
 *
 * {@link closed} is a replaying subject, not a plain one: a second `open` disposes the first panel
 * synchronously, so the first ref's dismissal can be reported before its caller has had a chance to
 * subscribe. Replaying one value means that caller still learns its panel is gone.
 */
export class ClarityFlyInRef<R = unknown> {
	/**
	 * Emits once — the body's result, or `undefined` for a dismissal — and completes.
	 *
	 * No `$` suffix: the member name is this library's agreed public contract and is shared verbatim
	 * with `ClarityDialogRef`, so spelling it Finnish here would make the two primitives' handles
	 * read differently at every call site for no gain.
	 */
	// eslint-disable-next-line rxjs-x/finnish -- agreed public contract, see the doc comment above
	public readonly closed: Observable<R | undefined>;

	/** Whether the panel's own header close control is switched off — see {@link setHeaderCloseDisabled}. */
	public readonly headerCloseDisabled: Signal<boolean>;

	/** The body's control drawn ahead of the heading — see {@link setHeaderLeading}. */
	public readonly headerLeading: Signal<TemplateRef<unknown> | null>;

	/** The body's controls drawn in the panel's header beside the close — see {@link setHeaderActions}. */
	public readonly headerActions: Signal<TemplateRef<unknown> | null>;

	/** The body's controls pinned in the panel's footer, below the scroll — see {@link setFooterActions}. */
	public readonly footerActions: Signal<TemplateRef<unknown> | null>;

	/** The heading the body set over the open-time `title` — see {@link setTitle}. */
	public readonly titleOverride: Signal<string | null>;

	/** The line the body set under the heading over the open-time `subtitle` — see {@link setSubtitle}. */
	public readonly subtitleOverride: Signal<string | null>;

	private readonly closedSubject = new ReplaySubject<R | undefined>(1);
	private readonly overlayRef: OverlayRef;
	private readonly headerCloseDisabledState = signal(false);
	private readonly headerLeadingState = signal<TemplateRef<unknown> | null>(null);
	private readonly headerActionsState = signal<TemplateRef<unknown> | null>(null);
	private readonly footerActionsState = signal<TemplateRef<unknown> | null>(null);
	private readonly titleOverrideState = signal<string | null>(null);
	private readonly subtitleOverrideState = signal<string | null>(null);
	/** The element focus returns to after a panel-owned dismissal; only a `first-focusable` panel keeps one. */
	private readonly restoreTarget: HTMLElement | null;
	/** Listeners and timers that must not outlive the panel, released by every close path. */
	private readonly teardowns: Array<() => void> = [];

	private dismissible: boolean;
	private isClosed = false;
	/** Takes the reader's close requests while set — see {@link setCloseGuard}. */
	private closeGuard: (() => void) | null = null;

	constructor(overlayRef: OverlayRef, dismissible?: boolean, behaviour?: ClarityFlyInRefBehaviour) {
		this.overlayRef = overlayRef;
		this.dismissible = dismissible ?? DEFAULT_DISMISSIBLE;
		this.closed = this.closedSubject.asObservable();
		this.headerCloseDisabled = this.headerCloseDisabledState.asReadonly();
		this.headerLeading = this.headerLeadingState.asReadonly();
		this.headerActions = this.headerActionsState.asReadonly();
		this.footerActions = this.footerActionsState.asReadonly();
		this.titleOverride = this.titleOverrideState.asReadonly();
		this.subtitleOverride = this.subtitleOverrideState.asReadonly();

		const isSidePanel = behaviour?.focus === 'first-focusable';
		this.restoreTarget = behaviour?.focus === 'first-focusable' ? activeHtmlElement(behaviour.document) : null;

		const escape = behaviour?.escape ?? 'top-most';
		this.dismissOnBackdropAndTopMostEscape(escape === 'top-most');
		if (behaviour !== undefined) {
			this.dismissOnDocumentEscape(behaviour);
		}

		if (isSidePanel) {
			this.focusFirstAfterOpen();
			this.stopKeysAtPanel();
		}
	}

	/**
	 * Disposes the panel and reports `result` to {@link closed}. Works regardless of
	 * {@link setDismissible} and {@link setHeaderCloseDisabled}, and is a no-op once the panel is
	 * already gone — a backdrop press landing on the same turn as a body's own close must not emit
	 * twice. Focus is never restored from here: a body that closes its panel decides where focus goes.
	 */
	public close(result?: R): void {
		if (this.isClosed) {
			return;
		}

		this.isClosed = true;
		for (const teardown of this.teardowns) {
			teardown();
		}
		this.overlayRef.dispose();
		this.closedSubject.next(result);
		this.closedSubject.complete();
	}

	/** Suppresses or restores Escape and backdrop dismissal while the panel is open. */
	public setDismissible(dismissible: boolean): void {
		this.dismissible = dismissible;
	}

	/** Sets the panel's inline size in px while it is open; the next open starts from its default width again. */
	public setWidth(width: number): void {
		this.overlayRef.updateSize({ width });
	}

	/**
	 * Switches the panel's own header close control off or back on while the panel is open.
	 *
	 * Separate from {@link setDismissible} because brain blocks them separately: a panel that must not
	 * be walked away from — the reveal-once token state — takes both, so Escape, a backdrop press and
	 * the header close all do nothing and only the body can close it.
	 */
	public setHeaderCloseDisabled(disabled: boolean): void {
		this.headerCloseDisabledState.set(disabled);
	}

	/**
	 * Stamps `template` in the panel's header ahead of the heading, or clears it with `null` — a step's way back (a
	 * detail's arrow to its list). Like {@link setHeaderActions}, the template is the body's, it sits outside the heading
	 * so the accessible name stays the title alone, and the body clears it when the step is left.
	 */
	public setHeaderLeading(template: TemplateRef<unknown> | null): void {
		this.headerLeadingState.set(template);
	}

	/**
	 * Stamps `template` in the panel's header, between the heading and the close control, or clears it with `null`.
	 *
	 * For a body whose own actions belong to the panel's chrome — a Domains card's type and Archive. The template is
	 * the body's, so it binds the body's state and resolves the body's injector; the body clears it when those actions
	 * no longer apply. It sits outside the heading, so the panel's accessible name stays the title alone.
	 */
	public setHeaderActions(template: TemplateRef<unknown> | null): void {
		this.headerActionsState.set(template);
	}

	/**
	 * Stamps `template` in the panel's footer, pinned below the scrolling body and aligned to its end, or clears it with
	 * `null` — the action fly-in's footer band. For a body whose own actions must stay reachable without reading to the
	 * end (a registry editor's Cancel and Save). Like {@link setHeaderActions}, the template is the body's and the body
	 * clears it when those actions no longer apply.
	 */
	public setFooterActions(template: TemplateRef<unknown> | null): void {
		this.footerActionsState.set(template);
	}

	/**
	 * Replaces the open-time `title` while the panel is open, or restores it with `null` — for a body whose heading is
	 * known only once its read answers (an editor showing the loaded record's name). The value is still the panel's
	 * accessible name, and a blank one counts as no heading, exactly as a blank `title` does.
	 */
	public setTitle(title: string | null): void {
		this.titleOverrideState.set(title);
	}

	/**
	 * Replaces the open-time `subtitle` while the panel is open, or restores it with `null` — the line under a heading
	 * set through {@link setTitle} (an editor showing the loaded record's key). It stays outside the accessible name, and
	 * a blank one renders nothing, exactly as a blank `subtitle` does.
	 */
	public setSubtitle(subtitle: string | null): void {
		this.subtitleOverrideState.set(subtitle);
	}

	/**
	 * Hands the reader's close requests — the header close, a backdrop press and Escape — to `guard` instead of closing,
	 * or restores the default with `null`. The guard decides whether the panel closes: an editor with unsaved changes
	 * asks first, and closes through its own exit. {@link setDismissible} and {@link setHeaderCloseDisabled} still gate
	 * the requests before the guard sees them, and {@link close} is never guarded.
	 */
	public setCloseGuard(guard: (() => void) | null): void {
		this.closeGuard = guard;
	}

	/**
	 * A close the reader asked for through the header close: the close guard when the body set one, otherwise
	 * {@link close}. A no-op once the panel is gone.
	 */
	public requestClose(): void {
		if (this.isClosed) {
			return;
		}
		if (this.closeGuard !== null) {
			this.closeGuard();

			return;
		}

		this.close();
	}

	/**
	 * The backdrop, and — for a `top-most` panel only — the dispatcher's Escape, as one gated stream.
	 * Any other policy leaves `keydownEvents()` unsubscribed, which is what makes the dispatcher skip
	 * this overlay and hand the Escape to the panel beneath. A backdrop press is
	 * `SidePanel.handleClose` (`SidePanel.tsx:268-272`), which restores focus.
	 */
	private dismissOnBackdropAndTopMostEscape(topMostEscape: boolean): void {
		const escape$ = topMostEscape
			? this.overlayRef.keydownEvents().pipe(filter(event => event.key === ESCAPE_KEY))
			: EMPTY;

		merge(this.overlayRef.backdropClick(), escape$)
			.pipe(
				filter(() => this.dismissible),
				takeUntil(this.closedSubject),
			)
			.subscribe(() => this.dismiss(true));
	}

	/** Brain's panels: Escape through a `document` listener; `top-most` and `none` install none. */
	private dismissOnDocumentEscape(behaviour: ClarityFlyInRefBehaviour): void {
		const removeListener = listenForDocumentEscape(behaviour.document, behaviour.escape, {
			isDismissible: () => this.dismissible,
			dismiss: restoreFocus => this.dismiss(restoreFocus),
		});
		if (removeListener !== undefined) {
			this.teardowns.push(removeListener);
		}
	}

	/** `SidePanel.tsx:232-247`: the first visible focusable element, once the panel has rendered. */
	private focusFirstAfterOpen(): void {
		const timer = setTimeout(() => focusFirstFocusable(this.overlayRef.overlayElement), FOCUS_DELAY_MS);

		this.teardowns.push(() => clearTimeout(timer));
	}

	/**
	 * Every key but Escape typed inside the panel stops at it, as in brain: page-level shortcuts on
	 * `document` never see them, and brain's own Tab trap never runs from inside (brain defect #37).
	 */
	private stopKeysAtPanel(): void {
		const pane = this.overlayRef.overlayElement;

		pane.addEventListener('keydown', stopKeyUnlessEscape);
		this.teardowns.push(() => pane.removeEventListener('keydown', stopKeyUnlessEscape));
	}

	/**
	 * A dismissal the panel itself owns. Only a `first-focusable` panel holds a restore target, and
	 * brain restores it only when the element is still in the document (`SidePanel.tsx:211-214`).
	 * A guarded panel restores nothing: its guard decides whether it closes at all.
	 */
	private dismiss(restoreFocus: boolean): void {
		if (this.closeGuard !== null) {
			this.closeGuard();

			return;
		}

		this.close();

		if (restoreFocus && this.restoreTarget?.isConnected === true) {
			this.restoreTarget.focus();
		}
	}
}
